export const SPELL_INITIAL_CHECK_DIRECT = 'direct';
export const SPELL_INITIAL_CHECK_SAVE = 'save';
export const SPELL_INITIAL_CHECK_ATTACK = 'attack';

export function resolveInitialTargetChecks(spell = {}, fallbackSaveDC = null) {
  if (spell?.spellType === 'damage' && spell?.attackType === 'save' && spell?.saveAbility) {
    return {
      mode: SPELL_INITIAL_CHECK_SAVE,
      saveType: spell.saveAbility,
      saveDC: Number.isFinite(spell?.saveDC) ? spell.saveDC : fallbackSaveDC,
      saveEffect: spell.saveEffect || (spell?.halfOnSave ? 'half' : 'negates'),
    };
  }

  if (spell?.spellType === 'damage' && (spell?.attackType === 'melee' || spell?.attackType === 'ranged')) {
    return {
      mode: SPELL_INITIAL_CHECK_ATTACK,
      attackType: spell.attackType,
      saveType: null,
      saveDC: null,
      saveEffect: null,
    };
  }

  return {
    mode: SPELL_INITIAL_CHECK_DIRECT,
    attackType: null,
    saveType: null,
    saveDC: null,
    saveEffect: spell?.saveEffect || null,
  };
}

export function resolveSpellAttackOutcome({
  attackRoll = null,
  attackBonus = 0,
  targetAC = null,
} = {}) {
  const d20 = Number.isFinite(attackRoll?.selected)
    ? attackRoll.selected
    : (parseInt(attackRoll, 10) || 0);
  const normalizedBonus = parseInt(attackBonus, 10) || 0;
  const total = d20 + normalizedBonus;
  const isCrit = d20 === 20;
  const isNatMiss = d20 === 1;
  const canCompareArmorClass = Number.isFinite(targetAC);
  const isHit = !isNatMiss && (isCrit || (canCompareArmorClass && total >= targetAC));

  return {
    d20,
    attackBonus: normalizedBonus,
    total,
    targetAC,
    isCrit,
    isHit,
    isMiss: !isHit,
    isNatMiss,
  };
}

export function resolveSpellSaveOutcomeAmount(spell = {}, baseAmount = 0, passed = false) {
  const normalizedBase = Math.max(0, parseInt(baseAmount, 10) || 0);
  if (!passed) {
    return {
      appliedAmount: normalizedBase,
      outcome: 'failed-save',
    };
  }

  const saveEffect = spell?.saveEffect || (spell?.halfOnSave ? 'half' : 'negates');
  if (saveEffect === 'none') {
    return {
      appliedAmount: normalizedBase,
      outcome: 'passed-save-none',
    };
  }
  if (saveEffect === 'half' || spell?.halfOnSave) {
    return {
      appliedAmount: Math.floor(normalizedBase / 2),
      outcome: 'passed-save-half',
    };
  }

  return {
    appliedAmount: 0,
    outcome: 'passed-save-negates',
  };
}

export function resolveInitialSpellOutcomes({
  spell,
  targetEntries = [],
  saveResults = [],
  saveDC = null,
} = {}) {
  const resultsByTarget = new Map((saveResults || []).map((result) => [result.combatantId, result]));
  return (targetEntries || []).map((entry) => {
    const saveResult = resultsByTarget.get(entry.targetId);
    const passed = !!saveResult && Number.isFinite(saveDC) && saveResult.total >= saveDC;
    const resolution = resolveSpellSaveOutcomeAmount(spell, entry.amount, passed);
    return {
      ...entry,
      saveResult: saveResult || null,
      passedSave: passed,
      saveDC,
      appliedAmount: resolution.appliedAmount,
      outcome: resolution.outcome,
    };
  });
}

export function hasPreAppliedTargetDebuffPercent(breakdown = []) {
  return (breakdown || []).some((entry) => entry?.type === 'debuff_percent');
}
